import {Injectable} from "@angular/core";
import {StandaloneFormService} from "src/app/umbrella-common/services/standalone-form.service";
import {BaseOrderFormService} from "../../../common/base-order-form.service";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {Store} from "@ngrx/store";
import {AppState} from "../../../../../store/state/app.state";
import {TranslateService} from "@ngx-translate/core";
import {BillingInformation, Order} from "../../../../../store/state/order.state";
import {UmbrellaValidators} from "../../../../../umbrella-common/validation/umbrella-validators";
import {OrderActions} from "../../../../../store/actions/order.actions";

@Injectable()
export class BillingPreferencesFomService extends StandaloneFormService implements BaseOrderFormService {

	constructor(translate: TranslateService, private fb: FormBuilder, private store: Store<AppState>) {
		super(translate);
	}

	protected buildFormFor(formKey: string, [billingInfo]: [BillingInformation]) {
		const form: FormGroup = this.fb.group({
			isPurchaseNumberRequiredForPayment: this.fb.control(billingInfo.isPurchaseNumberRequiredForPayment),
			sameCompanyAndContactPersonAsProvided: this.fb.control(billingInfo.sameCompanyAndContactPersonAsProvided),
			coverTravelExpenses: this.fb.control(billingInfo.coverTravelExpenses),
			additionalInformation: this.fb.control(billingInfo.additionalInformation, [UmbrellaValidators.textLengthBetween(3, 5000)])
		});

		if (form.controls['isPurchaseNumberRequiredForPayment'].value) {
			form.addControl('purchaseOrderNumber', new FormControl(billingInfo.purchaseOrderNumber, [Validators.required, UmbrellaValidators.textLengthBetween(3, 5000)]));
		}

		this.initFormControls(form);
		return form;
	}

	getControlNamesInOrder(): string[] {
		return [];
	}

	fillOrder(order: Order): Order {
		order.billingInformations = this.isFormInitialized() ? this.getBillingInformation(order) : null;
		return order;
	}

	updatePaymentRequired(required: boolean) {
		let form = this.getForm(StandaloneFormService.FORM_KEY);
		if (!required) {
			this.store.dispatch(OrderActions.SET_PURCHASE_NUMBER({purchaseOrderNumber: null}));
			form.removeControl('purchaseOrderNumber');
		} else {
			form.addControl('purchaseOrderNumber', new FormControl('', [Validators.required, UmbrellaValidators.textLengthBetween(3, 5000)]));
			form.controls['purchaseOrderNumber'].valueChanges.pipe()
				.subscribe(purchaseOrderNumber => this.store.dispatch(OrderActions.SET_PURCHASE_NUMBER({purchaseOrderNumber})));
		}
		this.updateFormControl('isPurchaseNumberRequiredForPayment', required, StandaloneFormService.FORM_KEY);
	}

	private getBillingInformation(order: Order): BillingInformation {
		const isPurchaseRequired = this.getValueFor('isPurchaseNumberRequiredForPayment', StandaloneFormService.FORM_KEY);
		let additionalInformation = this.getValueFor('additionalInformation', StandaloneFormService.FORM_KEY);
		return {
			sameCompanyAndContactPersonAsProvided: this.getValueFor('sameCompanyAndContactPersonAsProvided', StandaloneFormService.FORM_KEY),
			isPurchaseNumberRequiredForPayment: isPurchaseRequired,
			purchaseOrderNumber: isPurchaseRequired ? this.getValueFor('purchaseOrderNumber', StandaloneFormService.FORM_KEY) : null,
			coverTravelExpenses: isPurchaseRequired ? this.getValueFor('coverTravelExpenses', StandaloneFormService.FORM_KEY) : false,
			additionalInformation: additionalInformation === "" ? null : additionalInformation,
			billingCompany: order.billingInformations?.billingCompany,
			billingContactPerson: order.billingInformations?.billingContactPerson,
		};
	}

	private initFormControls(form: FormGroup) {
		form.controls['sameCompanyAndContactPersonAsProvided'].valueChanges.subscribe(sameCompanyAndContactPersonAsProvided => {
			this.store.dispatch(OrderActions.TOGGLE_SAME_COMPANY({sameCompanyAndContactPersonAsProvided}));
		});
		form.controls['isPurchaseNumberRequiredForPayment'].valueChanges.subscribe(isPurchaseNumberRequiredForPayment => {
			this.store.dispatch(OrderActions.TOGGLE_PURCHASE_PAYMENT({isPurchaseNumberRequiredForPayment}));
			!isPurchaseNumberRequiredForPayment && form.controls['coverTravelExpenses'].setValue(false);
		});
		form.controls['coverTravelExpenses'].valueChanges.subscribe(coverTravelExpenses => this.store.dispatch(OrderActions.TOGGLE_TRAVEL_EXPENSE({coverTravelExpenses})));
		form.controls['additionalInformation'].valueChanges.subscribe(additionalInformation => this.store.dispatch(OrderActions.SET_ADDITIONAL_BILLING_INFORMATION({additionalInformation})));

		if (form.controls['isPurchaseNumberRequiredForPayment'].value) {
			form.controls['purchaseOrderNumber'].valueChanges.subscribe(purchaseOrderNumber => this.store.dispatch(OrderActions.SET_PURCHASE_NUMBER({purchaseOrderNumber})));
		}
	}
}
